import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  FormControl,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import useFetchObject from '../../api/useFetchObject';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

const RoleEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionIds: []
  });

  const [validationError, setValidationError] = useState('');

  // Fetch role data
  const {
    data: role,
    isLoading: roleLoading,
    isError: roleError,
    error: roleErrorDetails,
  } = useFetchObject('roles', id, token);

  // Fetch permissions
  const {
    data: permissions,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useFetchObjects('permissions', 'permissions', token);

  // Update mutation
  const {
    handleUpdate,
    loading: isSubmitting,
  } = useUpdate('roles', id, token, '/roles',
    t('roles.update_success', 'Role updated successfully'),
    t('roles.update_error', 'Failed to update role')
  );

  // Group permissions by their type/category
  const [permissionGroups, setPermissionGroups] = useState([]);

  // Populate form data when role data is loaded
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        permissionIds: Array.isArray(role.permissions) ? 
          role.permissions.map(p => p.id) : []
      });
    }
  }, [role]);

  // Group permissions by category
  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const groups = {};
      
      // Process permissions into groups by their code prefix
      permissions.forEach(permission => {
        // Split the code by underscore and take the first part as group
        const groupName = permission.code?.split('_')[0] || 'other';
        if (!groups[groupName]) {
          groups[groupName] = {
            name: groupName,
            label: t(`permissions.groups.${groupName}`, groupName),
            permissions: []
          };
        }
        groups[groupName].permissions.push(permission);
      });
      
      setPermissionGroups(Object.values(groups));
    }
  }, [permissions, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    const permissionId = name;

    setFormData(prev => {
      if (checked) {
        return {
          ...prev,
          permissionIds: [...prev.permissionIds, permissionId]
        };
      } else {
        return {
          ...prev,
          permissionIds: prev.permissionIds.filter(id => id !== permissionId)
        };
      }
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setValidationError(t('validation.role_name_required', 'Role name is required'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      handleUpdate(formData);
    }
  };

  // Helper function to determine if a role is a system role
  const isSystemRole = (roleName) => {
    return ['admin', 'user', 'basic user', 'manager'].includes(
      (roleName || '').toLowerCase()
    );
  };

  if (roleLoading || permissionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (roleError) {
    return (
      <Box>
        <Alert severity="error">
          {roleErrorDetails?.message || t('roles.fetch_error', 'Failed to load role')}
        </Alert>
        <Button
          component={RouterLink}
          to="/roles"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          {t('common.back_to_list', 'Back to List')}
        </Button>
      </Box>
    );
  }

  const isSystem = role ? isSystemRole(role.name) : false;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={RouterLink}
          to="/roles"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {t('roles.edit_title', 'Edit Role')}
        </Typography>
        {isSystem && (
          <Chip 
            label={t('roles.system', 'System Role')}
            color="primary"
          />
        )}
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {permissionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('permissions.fetch_error', 'Failed to load permissions')}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('roles.basic_info', 'Basic Information')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('roles.name', 'Role Name')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isSystem}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                  {t('roles.id', 'ID')}:
                </Typography>
                <Chip label={id} />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('roles.description', 'Description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                disabled={isSystem}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('roles.permissions', 'Permissions')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('roles.permissions_info', 'Select the permissions for this role')}
        </Typography>
        
        {permissionGroups.map((group) => (
          <Box key={group.name} sx={{ mb: 3 }}>
            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                {group.label}
              </FormLabel>
              <FormGroup>
                <Grid container spacing={2}>
                  {group.permissions.map((permission) => (
                    <Grid item xs={12} sm={6} md={3} key={permission.id}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            name={permission.id} 
                            checked={formData.permissionIds.includes(permission.id)}
                            onChange={handlePermissionChange}
                            disabled={isSystem}
                          />
                        }
                        label={permission.name}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </FormControl>
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/roles"
          disabled={isSubmitting}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          variant="contained"
          type="submit"
          disabled={isSystem || isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t('common.save', 'Save')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default RoleEdit; 